import React from "react";
import {
  Box,
  Button,
  Chip,
  Fade,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { productsApi } from "../../api/products";
import { settingsApi } from "../../api/settings";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { normalizeProjectModules } from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";
import {
  buildTrialMissions,
  isTrialProject,
  TRIAL_LANDING_EVENT,
  TRIAL_PRODUCTS_EVENT,
  TRIAL_REMINDER_MS,
  TRIAL_SOUND_STORAGE_KEY,
} from "../../utils/trialOnboarding";

function readStorage(key, fallback = "") {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
}

function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // La guía continúa aunque el navegador bloquee localStorage.
  }
}

function isWritingNow() {
  const target = document.activeElement;
  const tagName = String(target?.tagName || "").toLowerCase();
  return Boolean(
    target?.isContentEditable ||
      tagName === "input" ||
      tagName === "textarea" ||
      tagName === "select",
  );
}

function missionIcon(mission) {
  if (mission?.id === "product") return <Inventory2RoundedIcon fontSize="small" />;
  if (mission?.id === "business") return <SettingsRoundedIcon fontSize="small" />;
  return <PublicRoundedIcon fontSize="small" />;
}

export default function TrialOnboardingCoach() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const superAdmin = isSuperAdmin(user);
  const projectKey = activeProject?.slug || activeProject?.id || activeProject?._id || "";
  const modules = normalizeProjectModules(activeProject?.modules);
  const eligible = Boolean(activeProject && projectKey && isTrialProject(activeProject) && !superAdmin);
  const isTaskRoute =
    location.pathname.includes("/productos") ||
    location.pathname.includes("/pagina") ||
    location.pathname.includes("/configuracion/suscripcion");

  const [productCount, setProductCount] = React.useState(null);
  const [landingSettings, setLandingSettings] = React.useState(null);
  const [visible, setVisible] = React.useState(true);
  const [pulseVersion, setPulseVersion] = React.useState(0);
  const [soundEnabled, setSoundEnabled] = React.useState(
    () => readStorage(TRIAL_SOUND_STORAGE_KEY, "1") !== "0",
  );
  const audioContextRef = React.useRef(null);

  const ready = Boolean(
    eligible &&
      (!modules.products || productCount !== null) &&
      (!modules.landing || landingSettings !== null),
  );

  const progress = React.useMemo(
    () =>
      buildTrialMissions({
        settings: landingSettings || {},
        productCount: Number(productCount || 0),
        modules,
      }),
    [landingSettings, modules, productCount],
  );

  const getAudioContext = React.useCallback(() => {
    if (typeof window === "undefined") return null;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioContextRef.current) audioContextRef.current = new AudioContextClass();
    return audioContextRef.current;
  }, []);

  const playReminderTone = React.useCallback(async () => {
    if (!soundEnabled) return;
    try {
      const context = getAudioContext();
      if (!context) return;
      if (context.state === "suspended") await context.resume();
      if (context.state !== "running") return;

      const start = context.currentTime + 0.02;
      [
        { frequency: 620, offset: 0 },
        { frequency: 830, offset: 0.16 },
      ].forEach(({ frequency, offset }) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const toneStart = start + offset;
        const toneEnd = toneStart + 0.11;

        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(frequency, toneStart);
        gain.gain.setValueAtTime(0.0001, toneStart);
        gain.gain.exponentialRampToValueAtTime(0.04, toneStart + 0.018);
        gain.gain.exponentialRampToValueAtTime(0.0001, toneEnd);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(toneStart);
        oscillator.stop(toneEnd + 0.02);
      });
    } catch {
      // Los navegadores pueden bloquear audio hasta la primera interacción.
    }
  }, [getAudioContext, soundEnabled]);

  React.useEffect(() => {
    if (!eligible) {
      setProductCount(null);
      setLandingSettings(null);
      return undefined;
    }

    let cancelled = false;
    setVisible(true);
    setProductCount(modules.products ? null : 0);
    setLandingSettings(modules.landing ? null : {});

    const requests = [];
    if (modules.products) requests.push(productsApi.list(projectKey));
    else requests.push(Promise.resolve({ products: [] }));
    if (modules.landing) requests.push(settingsApi.get(projectKey));
    else requests.push(Promise.resolve({ settings: {} }));

    Promise.allSettled(requests).then(([productsResult, settingsResult]) => {
      if (cancelled) return;
      const productsData = productsResult.status === "fulfilled" ? productsResult.value : {};
      const settingsData = settingsResult.status === "fulfilled" ? settingsResult.value : {};
      setProductCount(Array.isArray(productsData?.products) ? productsData.products.length : 0);
      setLandingSettings(settingsData?.settings || {});
    });

    return () => {
      cancelled = true;
    };
  }, [eligible, modules.landing, modules.products, projectKey]);

  React.useEffect(() => {
    const handleProductsUpdated = (event) => {
      const detail = event?.detail || {};
      if (String(detail.projectId || "") !== String(projectKey)) return;
      const nextCount = Number(detail.count);
      if (Number.isFinite(nextCount)) setProductCount(Math.max(0, nextCount));
    };
    const handleLandingUpdated = (event) => {
      const detail = event?.detail || {};
      if (String(detail.projectId || "") !== String(projectKey)) return;
      if (detail.settings) setLandingSettings(detail.settings);
    };

    window.addEventListener(TRIAL_PRODUCTS_EVENT, handleProductsUpdated);
    window.addEventListener(TRIAL_LANDING_EVENT, handleLandingUpdated);
    return () => {
      window.removeEventListener(TRIAL_PRODUCTS_EVENT, handleProductsUpdated);
      window.removeEventListener(TRIAL_LANDING_EVENT, handleLandingUpdated);
    };
  }, [projectKey]);

  React.useEffect(() => {
    if (!eligible || !soundEnabled) return undefined;
    const unlockAudio = () => {
      const context = getAudioContext();
      if (context?.state === "suspended") context.resume().catch(() => null);
    };
    document.addEventListener("pointerdown", unlockAudio, true);
    document.addEventListener("keydown", unlockAudio, true);
    return () => {
      document.removeEventListener("pointerdown", unlockAudio, true);
      document.removeEventListener("keydown", unlockAudio, true);
    };
  }, [eligible, getAudioContext, soundEnabled]);

  React.useEffect(() => {
    if (!ready || progress.complete || isTaskRoute) return undefined;

    const remind = () => {
      const dialogOpen = Boolean(document.querySelector('[role="dialog"]'));
      if (document.hidden || dialogOpen || isWritingNow()) return;
      setVisible(true);
      setPulseVersion((current) => current + 1);
      playReminderTone();
    };

    const intervalId = window.setInterval(remind, TRIAL_REMINDER_MS);
    return () => window.clearInterval(intervalId);
  }, [isTaskRoute, playReminderTone, progress.complete, ready]);

  React.useEffect(
    () => () => {
      const context = audioContextRef.current;
      if (context && context.state !== "closed") context.close().catch(() => null);
    },
    [],
  );

  if (!ready || isTaskRoute) return null;

  const nextMission = progress.nextMission;
  const complete = progress.complete;
  const title = complete ? "Tu prueba ya está armada" : nextMission?.label;
  const description = complete
    ? "Activá el plan para cargar más publicaciones y mantener todas las funciones habilitadas."
    : nextMission?.description;

  const handlePrimaryAction = () => {
    setVisible(false);
    if (complete) {
      navigate(`/dashboard/proyectos/${projectKey}/configuracion/suscripcion`);
      return;
    }
    if (nextMission?.section === "products") {
      navigate(`/dashboard/proyectos/${projectKey}/productos?crear=1`);
      return;
    }
    navigate(`/dashboard/proyectos/${projectKey}/pagina?mision=${nextMission?.routeKey || "datos"}`);
  };

  const toggleSound = () => {
    const nextValue = !soundEnabled;
    setSoundEnabled(nextValue);
    writeStorage(TRIAL_SOUND_STORAGE_KEY, nextValue ? "1" : "0");
    if (nextValue) {
      const context = getAudioContext();
      if (context?.state === "suspended") context.resume().catch(() => null);
    }
  };

  return (
    <Fade in={visible} timeout={240}>
      <Paper
        key={pulseVersion}
        elevation={18}
        role="status"
        aria-live="polite"
        sx={{
          position: "fixed",
          zIndex: (theme) => theme.zIndex.snackbar,
          right: { xs: 14, sm: 22 },
          left: { xs: 14, sm: "auto" },
          bottom: { xs: 14, sm: 22 },
          width: { sm: 370 },
          maxWidth: "calc(100vw - 28px)",
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid rgba(243,90,58,0.25)",
          boxShadow: "0 24px 70px rgba(25,36,47,0.24)",
          "@keyframes trialCoachPulse": {
            "0%": { transform: "translateY(0) scale(1)" },
            "45%": { transform: "translateY(-4px) scale(1.012)" },
            "100%": { transform: "translateY(0) scale(1)" },
          },
          animation: pulseVersion ? "trialCoachPulse .55s ease" : "none",
        }}
      >
        <Box
          sx={{
            px: 2.25,
            pt: 2,
            pb: 1.7,
            color: "#fff",
            background: complete
              ? "linear-gradient(135deg,#19242F 0%,#30485B 100%)"
              : "linear-gradient(135deg,#F35A3A 0%,#F1783D 48%,#19242F 140%)",
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.25}>
            <Box
              sx={{
                width: 42,
                height: 42,
                flex: "0 0 auto",
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.17)",
                border: "1px solid rgba(255,255,255,0.22)",
              }}
            >
              {complete ? <RocketLaunchRoundedIcon /> : <AutoAwesomeRoundedIcon />}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={complete ? "LISTO PARA ACTIVAR" : "SIGUIENTE MISIÓN"}
                  sx={{
                    height: 23,
                    color: "#fff",
                    bgcolor: "rgba(25,36,47,0.22)",
                    fontWeight: 950,
                    fontSize: 10,
                    letterSpacing: 0.7,
                  }}
                />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.78)", fontWeight: 800 }}>
                  {progress.completedCount}/{progress.totalCount} completas
                </Typography>
              </Stack>
              <Typography sx={{ mt: 0.75, fontWeight: 950, lineHeight: 1.18, fontSize: 18 }}>
                {title}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.25}>
              <Tooltip title={soundEnabled ? "Silenciar recordatorios" : "Activar sonidos"}>
                <IconButton
                  size="small"
                  onClick={toggleSound}
                  aria-label={soundEnabled ? "Silenciar recordatorios" : "Activar sonidos"}
                  sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.1)" }}
                >
                  {soundEnabled ? <VolumeUpRoundedIcon fontSize="small" /> : <VolumeOffRoundedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Cerrar por ahora">
                <IconButton
                  size="small"
                  onClick={() => setVisible(false)}
                  aria-label="Cerrar guía por ahora"
                  sx={{ color: "#fff" }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: 2.25, bgcolor: "#fff" }}>
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.68)", lineHeight: 1.55 }}>
            {description}
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress.percent}
            sx={{
              mt: 1.8,
              mb: 1.5,
              height: 7,
              borderRadius: 999,
              bgcolor: "rgba(25,36,47,0.08)",
              "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: "#F35A3A" },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            startIcon={complete ? <RocketLaunchRoundedIcon /> : missionIcon(nextMission)}
            onClick={handlePrimaryAction}
            sx={{
              bgcolor: "#F35A3A",
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 950,
              "@keyframes trialActionGlow": {
                "0%, 100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.20)" },
                "50%": { boxShadow: "0 0 0 9px rgba(243,90,58,0)" },
              },
              animation: complete ? "none" : "trialActionGlow 1.9s ease-in-out infinite",
              "&:hover": { bgcolor: "#DB4B2F" },
            }}
          >
            {complete ? "Activar mi plan" : "Continuar misión"}
          </Button>

          {!complete ? (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1.25, color: "rgba(25,36,47,0.46)", textAlign: "center" }}
            >
              Recordatorio visual y sonoro cada 15 segundos.
            </Typography>
          ) : null}
        </Box>
      </Paper>
    </Fade>
  );
}
